import { v4 as uuidv4 } from "uuid";
import { getExtension, removeExtension } from "../src/taskpane/components/helpers/stringHelper";
import { IFileSaveResult } from "../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import { IFileToUpload } from "../src/taskpane/interfaces/IFileToUpload";
import { logger } from "./logger";
import { IPageSettings } from "../src/taskpane/interfaces/IPageSettings";
import { DirectoryTreeType } from "../src/taskpane/config/config";
import { resetContentManagerMoveTo, setContentManagerMoveTo, setContentManagerMoveToCopyJobResults, setContentManagerMoveToError } from "../src/taskpane/redux/modules/contentManagerDirectory";
import { IContentManagerMoveTo } from "../src/taskpane/redux/models/reduxModels";
import { ContentManagerCopyMoveModeEnum } from "../src/taskpane/components/enums/ContentManagerCopyMoveModeEnum";
import { Dispatch } from "@reduxjs/toolkit";
import authModule from "./AuthModule";
import { createCopyJobs, getListSettings } from "../src/taskpane/services/SharePointRestServices";
import { ICreateCopyJobsRequest } from "../src/taskpane/interfaces/ICreateCopyJobsRequest";
import { apiCompleteChunkUpload, apiCreateFolders, apiUpload } from "../src/taskpane/services/ApiService";
import { getItemCustomSelect, getSharePointSite } from "../src/taskpane/services/GraphService";
import { CopyMoveErrors, validSaveAsPdfExtentions } from "../src/taskpane/config/constants";
import { OneDriveUrlHelper } from "../src/taskpane/components/helpers/oneDriveUrlHelper";
import { resolveDroppedFileListSettingsTarget } from "../src/taskpane/components/helpers/dragDropListSettingsTarget";
import { ICompleteFileUploadRequest, IFileUploadProgress, IFileUploadRequest } from "../src/taskpane/interfaces/IFileUploadRequest";
import { AxiosRequestConfig } from "axios";
import { buildOutlookFileUploads, IOutlookDroppedItem } from "./outlookDroppedItems";
import { customToast } from "../src/taskpane/components/CustomToast";

export interface IFileSystemFile {
    id: string;
    file: File;
}

export interface IFileSystemFolder {
    id: string;
    name: string;
    path: string;
    parentFolderId: string;
    serverRelativeUrl?: string;
}

export interface IFileSystemFileSaveResult {
    success: boolean;
    name: string;
}

export enum DragMode {
    files = 0,
    gridItems = 1,
    exchangeItems = 2
}

interface IUploadConfig {
    concurrency: number;
    minChunkSize: number;
    maxChunkSize: number;
    maxSingleShotSize: number;
}

const uploadOptions: IUploadConfig = {
    concurrency: 4,
    minChunkSize: 1 * 1024 * 1024, // 1 MiB
    maxChunkSize: 5 * 1024 * 1024, // 5 MiB
    maxSingleShotSize: 10 * 1024 * 1024 // 10 MiB
}

export class DragAndDropHelper {
    public static isDroppedModeActive: boolean = false;
    public static fileEntries: IFileSystemFile[] = [];
    public static folderEntries: IFileSystemFolder[] = [];
    public static mode: DragMode = DragMode.files;
    public static canCreateFolder = true;

    public static readonly gridDataTransferType: string = "griditem";
    public static sourceDirectory: IPageSettings;
    public static destinationDirectory: IPageSettings;
    public static sourceServerRelPath: string;
    public static selectedGridItems: any = [];

    public static uploadProgress: IFileUploadProgress[] = [];

    static validateDroppedGridItems(event: React.DragEvent<HTMLDivElement>, isGrid?: boolean, gridItem?: any, homeItem?: any, treeItem?: any): boolean {
        try {
            if (event.dataTransfer.items?.length > 0) {
                for (let i = 0; i < event.dataTransfer.items.length; i++) {
                    if (event.dataTransfer.items[i].kind === "string" && event.dataTransfer.items[i].type === this.gridDataTransferType) {
                        if (homeItem && this.sourceDirectory?.id !== undefined && (this.sourceDirectory?.id === homeItem?.driveId || this.sourceDirectory?.id === homeItem?.itemId || this.selectedGridItems.some(x => x[".spItemUrl"].includes(homeItem?.itemId)))) {
                            return false;
                        }
                        if (isGrid && this.sourceDirectory?.id !== undefined && this.destinationDirectory?.id !== undefined && this.sourceDirectory?.id === this.destinationDirectory?.id) {
                            return false;
                        }
                        if (gridItem) {
                            if (this.selectedGridItems.some(x => x.ID === gridItem.ID)) {
                                return false;
                            }
                        }
                        if (treeItem && (treeItem.treeType === DirectoryTreeType.folder || treeItem.treeType === DirectoryTreeType.document)) {
                            if ((this.sourceDirectory?.id === (treeItem?.id) || (this.selectedGridItems.some(x => x[".spItemUrl"].includes(treeItem.id)) && treeItem.id !== this.sourceDirectory?.parentId))) {
                                return false;
                            }
                        }
                        return true;
                    }
                }
            }
        } catch (error) {
            logger(error, "DragAndDropHelper.ts (validateDroppedGridItems)");
        }
        return false;
    }

    static validateDroppedFiles(event: React.DragEvent<HTMLDivElement>): boolean {
        if (event.dataTransfer.items?.length > 0) {
            for (let i = 0; i < event.dataTransfer.items.length; i++) {
                if (event.dataTransfer.items[i].kind === "file") {
                    this.mode = DragMode.files;
                    return true;
                }
            }
        }
        return false;
    }

    static async handleDroppedFiles(event: React.DragEvent<HTMLDivElement>, item?: any): Promise<IFileToUpload[]> {
        let folderCreateEnabled = this.canCreateFolder;
        // Snapshot so an intake that yields NOTHING uploadable (e.g. an all-empty-files
        // drop) can leave global drop state exactly as it was — mirroring the
        // handleOutlookDroppedItems guard. Flipping isDroppedModeActive while fileEntries
        // is empty poisons every subsequent save: uploadDroppedFiles returns null and the
        // save flows dereference/abort on it.
        const priorFileEntries = this.fileEntries;
        const priorFolderEntries = this.folderEntries;
        const priorDroppedModeActive = this.isDroppedModeActive;
        this.skippedEmptyFileNames = [];
        this.clear();
        this.isDroppedModeActive = true;
        const filesToUpload: IFileToUpload[] = [];
        try {
            const fileSystemEntries: FileSystemEntry[] = [];
            if (event.dataTransfer.items?.length > 0) {
                for (let i = 0; i < event.dataTransfer.items.length; i++) {
                    const fileSystemEntry: FileSystemEntry = event.dataTransfer.items[i].webkitGetAsEntry();
                    if (fileSystemEntry) {
                        fileSystemEntries.push(fileSystemEntry);
                    }
                }

                if (fileSystemEntries?.length > 0) {
                    for (let i = 0; i < fileSystemEntries.length; i++) {
                        await this.traverseFileTree(fileSystemEntries[i], filesToUpload);
                    }
                }
            }
        } catch (error) {
            logger(error, "DragAndDropHelper.ts (handleDroppedFiles)");
        }

        if (item) {
            try {
                const listSettingsTarget = await resolveDroppedFileListSettingsTarget(
                    item,
                    async (siteId: string) => (await getSharePointSite(siteId))?.webUrl || ""
                );
                const isOneDrive: boolean = listSettingsTarget.isOneDrive;

                const accessToken = !isOneDrive
                    ? await authModule.getSharePointAccessToken()
                    : await authModule.getOneDriveAccessToken();

                const webUrl: string = !isOneDrive
                    ? listSettingsTarget.siteUrl
                    : OneDriveUrlHelper.getSiteUrl(listSettingsTarget.siteUrl);

                let listId: string = listSettingsTarget.listId;
                if (isOneDrive) {
                    const driveId: string = listSettingsTarget.driveId;
                    const itemId: string = listSettingsTarget.itemId;
                    if (driveId) {
                        const itemDetails = await getItemCustomSelect(await authModule.getGraphAccessToken(), driveId, itemId, "sharepointIds");
                        if (itemDetails) {
                            listId = itemDetails.sharepointIds?.listId;
                        }
                    }
                }

                const listSettings = webUrl && listId
                    ? await getListSettings(accessToken, webUrl, listId)
                    : null;

                folderCreateEnabled = listSettings ? listSettings.EnableFolderCreation : folderCreateEnabled
            } catch (error) {
                logger(error, "DragAndDropHelper.ts (getListSettings)");
            }
        }

        this.notifySkippedEmptyFiles();
        if (this.fileEntries.length === 0) {
            // Nothing uploadable survived the guards: restore the prior drop state instead
            // of leaving active-mode-with-no-entries (which breaks the next save).
            this.fileEntries = priorFileEntries;
            this.folderEntries = priorFolderEntries;
            this.isDroppedModeActive = priorDroppedModeActive;
            return [];
        }

        if (!folderCreateEnabled) {
            const rootFolder = filesToUpload.find(x => x.isFolder && !x.parentFolderId);

            const onlyFiles = filesToUpload.map(file => {
                if (!file.isFolder && !file.parentFolderId) {
                    return file;
                }

                if (!file.isFolder && file.parentFolderId === rootFolder?.id) {
                    let updatedFile = {
                        ...file
                    }
                    delete updatedFile.parentFolderId;

                    return updatedFile;
                }

                return null;
            }).filter(x => x);

            return this.reorderData(onlyFiles);
        }

        return this.reorderData(filesToUpload);
    }

    static handleSelectedFiles(event: React.ChangeEvent<HTMLInputElement>): IFileToUpload[] {
        // Same empty-intake protection as handleDroppedFiles: snapshot, and restore below
        // when nothing uploadable survives (e.g. the picker selected only 0-byte files).
        const priorFileEntries = this.fileEntries;
        const priorFolderEntries = this.folderEntries;
        const priorDroppedModeActive = this.isDroppedModeActive;
        this.skippedEmptyFileNames = [];
        this.clear();
        this.isDroppedModeActive = true;
        let filesToUpload: IFileToUpload[] = [];
        try {
            const files = (event.target as HTMLInputElement).files;
            if (files?.length > 0) {
                for (let i = 0; i < files.length; i++) {
                    const file: File = files[i];
                    if (this.shouldSkipEmptyFile(file, "DragAndDropHelper.ts (handleSelectedFiles)")) {
                        continue;
                    }
                    const fileToUpload: IFileToUpload = this.getFileToUpload(file);

                    if (file.webkitRelativePath?.length > 0) {
                        const fileFolderList: string[] = file.webkitRelativePath.split("/");
                        fileFolderList.pop();

                        let currentParentId: string | undefined = undefined;
                        let currentPath: string = "";

                        for (const folderName of fileFolderList) {
                            currentPath += "/" + folderName;

                            let folderEntry: IFileSystemFolder = this.folderEntries?.find(f => f.path === currentPath);

                            if (!folderEntry) {
                                const newFolder: IFileToUpload = this.getFolderToUpload(folderName, currentPath, currentParentId);
                                filesToUpload.push(newFolder);

                                folderEntry = { id: newFolder.id, name: folderName, path: currentPath, parentFolderId: currentParentId } as IFileSystemFolder;
                            }

                            currentParentId = folderEntry!.id;
                        }

                        fileToUpload.parentFolderId = currentParentId;
                    }

                    filesToUpload.push(fileToUpload);
                }
            }
        } catch (error) {
            logger(error, "DragAndDropHelper.ts (handleSelectedFiles)");
        }

        this.notifySkippedEmptyFiles();
        if (this.fileEntries.length === 0) {
            // Nothing uploadable survived the guards: restore the prior drop state instead
            // of leaving active-mode-with-no-entries (which breaks the next save).
            this.fileEntries = priorFileEntries;
            this.folderEntries = priorFolderEntries;
            this.isDroppedModeActive = priorDroppedModeActive;
            return [];
        }

        return this.reorderData(filesToUpload);
    }

    // Office.js (new Outlook / OWA) counterpart to handleDroppedFiles: sources files
    // from a DragAndDropEvent drop payload instead of an HTML DataTransfer. Mirrors
    // handleDroppedFiles' clear()-then-build so each drop is self-contained (the drop
    // advances to the Saving/Properties step, exactly like the desktop container drop).
    static handleOutlookDroppedItems(items: IOutlookDroppedItem[], noSubjectFallback: string): IFileToUpload[] {
        // Build first (pure, no state mutation). If nothing survives the content/naming
        // guards, leave global drop state untouched — an empty drop must not flip
        // isDroppedModeActive or wipe fileEntries, which would poison a subsequent save.
        const built = buildOutlookFileUploads(items, noSubjectFallback);
        if (built.length === 0) {
            return [];
        }

        this.clear();
        this.isDroppedModeActive = true;
        this.mode = DragMode.files;

        built.forEach(b => this.fileEntries.push({ id: b.fileToUpload.id, file: b.file }));

        return this.reorderData(built.map(b => b.fileToUpload));
    }

    static async handleGridMove(dispatch: Dispatch<any>, siteUrl: string, workingDirectories: IPageSettings[], isInOneDrive: boolean, destSerRelPathOverride?: string) {
        try {
            const moveToOperation: IContentManagerMoveTo = {
                mode: ContentManagerCopyMoveModeEnum.Move,
                error: "",
                moveToEnabled: false,
                workingDirectories,
                siteUrl,
                sourceSiteUrl: this.sourceDirectory.webUrl,
                sourceServerRelPath: this.sourceServerRelPath,
                busyMovingFiles: true,
                isMoveInProgress: true,
                copyJobResults: [],
                isMoveComplete: false,
                isMoveCancelled: false,
                sourceDirectory: this.sourceDirectory,
                destinationDirectory: this.destinationDirectory,
                progressCountCompleted: 0,
                progressCountTotal: this.selectedGridItems.length,
                selectedFiles: this.selectedGridItems.map(selectedFile => ({ ...selectedFile, isBusy: true }))
            }
            dispatch(setContentManagerMoveTo(moveToOperation));

            const copyJobRequest: ICreateCopyJobsRequest = {
                siteUrl,
                accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                files: this.selectedGridItems,
                sourceSiteUrl: moveToOperation.sourceSiteUrl,
                destinationServerRelPath: destSerRelPathOverride ? destSerRelPathOverride : DragAndDropHelper.destinationDirectory.serverRelativePath,
                isMoveMode: true
            }

            const copyJobResults = await createCopyJobs(copyJobRequest);
            if (copyJobResults?.success) {
                dispatch(setContentManagerMoveToCopyJobResults(copyJobResults.jobs));
            } else {
                // The full reset is right here — a drag-drop move starts immediately and
                // has no pending queue to preserve — but it also clears `error`, so the
                // message must be re-set afterwards, and must be TRUTHY: every error
                // indicator is gated on truthiness, and the API result carries no message
                // at all when the failure was a client-side throw.
                dispatch(resetContentManagerMoveTo());
                dispatch(setContentManagerMoveToError(copyJobResults?.error ? copyJobResults.error : CopyMoveErrors.Unknown));
            }
        } catch (error) {
            logger(error, "DragAndDropHelper.tsx (handleGridMove(createCopyJobs))");
            // Was reset-only: the move vanished with no indication it had ever been
            // attempted, let alone failed.
            dispatch(resetContentManagerMoveTo());
            dispatch(setContentManagerMoveToError(CopyMoveErrors.Unknown));
        }

        this.resetGridItems();
    }

    static async createFolders(accessToken: string, siteUrl: string, parentUrl: string, listId: string, autoFolderCreation: boolean, foldersToUpload: IFileToUpload[]): Promise<void> {
        if (this.folderEntries?.length > 0 && foldersToUpload?.length > 0) {
            const folderSystemEntriesToUpload: IFileToUpload[] = foldersToUpload.filter(f => f.isFileSystemEntry && f.isFolder);
            if (folderSystemEntriesToUpload.length > 0) {
                const folders: IFileSystemFolder[] = folderSystemEntriesToUpload
                    .map(folderEntry => this.folderEntries.find(f => f.id === folderEntry.id))
                    .filter(folderEntry => folderEntry);

                try {
                    this.folderEntries = await apiCreateFolders(accessToken, siteUrl, parentUrl, listId, autoFolderCreation, folders);
                } catch (error) {
                    logger(error, "DragAndDropHelper.ts (createFolders)");
                }
            }
        }
        return null;
    }

    static async uploadDroppedFiles(accountId: string, filesToUpload: IFileToUpload[], folderEntriesToUpload: IFileSystemFolder[]): Promise<IFileSaveResult | null> {
        // Clear any existing upload progress
        this.uploadProgress.length = 0;

        if (this.fileEntries?.length > 0 && filesToUpload?.length > 0) {
            const fileSystemEntriesToUpload: IFileToUpload[] = filesToUpload.filter(f => f.isFileSystemEntry && !f.isFolder);
            if (fileSystemEntriesToUpload.length > 0) {
                const fileEntriesToUpload: IFileSystemFile[] = [...this.fileEntries];
                const fileSystemUploadResults = await Promise.all(fileSystemEntriesToUpload.map(async fileEntry => {
                    const fileSystemFile: IFileSystemFile = fileEntriesToUpload.find(f => f.id === fileEntry.id && f.file);
                    if (fileSystemFile) {
                        try {
                            const folder: IFileSystemFolder | null = fileEntry.parentFolderId
                                ? folderEntriesToUpload?.find(f => f.id === fileEntry.parentFolderId)
                                : null;

                            let chunkSize: number;
                            let totalChunks: number;
                            if (fileSystemFile.file.size <= uploadOptions.maxSingleShotSize) {
                                totalChunks = 1;
                            }
                            else {
                                chunkSize = Math.max(uploadOptions.minChunkSize, Math.min(uploadOptions.maxChunkSize, fileSystemFile.file.size / 10));
                                totalChunks = Math.ceil(fileSystemFile.file.size / chunkSize);
                            }

                            const fileUploadProgress: IFileUploadProgress = {
                                fileName: fileSystemFile.file.name,
                                fileExtension: fileEntry.extension,
                                sizeString: this.formatBytes(fileSystemFile.file.size),
                                progress: 0,
                            };
                            this.uploadProgress.push(fileUploadProgress);

                            if (totalChunks > 1) {
                                const uploadId: string = uuidv4();
                                const fileChunks: Blob[] = [];

                                for (let i = 0; i < totalChunks; i++) {
                                    const start = i * chunkSize;
                                    const end = Math.min(fileSystemFile.file.size, start + chunkSize);
                                    fileChunks.push(fileSystemFile.file.slice(start, end));
                                }

                                let uploadedChunks: number = 0;
                                for (let i = 0; i < fileChunks.length; i += uploadOptions.concurrency) {
                                    const chunkSlices: Blob[] = fileChunks.slice(i, i + uploadOptions.concurrency);

                                    await Promise.all(chunkSlices.map(async (chunk, index) => {
                                        if (fileUploadProgress.isCancellationRequested) return;

                                        const chunkFormData = new FormData();
                                        chunkFormData.set("file", chunk, fileSystemFile.file.name);

                                        const uploadRequest: IFileUploadRequest = {
                                            accountId,
                                            uploadId,
                                            formData: chunkFormData,
                                            chunkIndex: (i + index),
                                            path: folder?.path
                                        };

                                        await apiUpload(uploadRequest);

                                        fileUploadProgress.progress = ++uploadedChunks / totalChunks;
                                    }));
                                }

                                if (fileUploadProgress.isCancellationRequested) {
                                    // Mark original file parameter as cancelled
                                    const fileToUpload: IFileToUpload = filesToUpload.find(f => f.id === fileEntry.id);
                                    if (fileToUpload) {
                                        fileToUpload.cancelled = true;
                                    }
                                }

                                const completeUploadRequest = {
                                    accountId,
                                    uploadId,
                                    fileName: fileSystemFile.file.name,
                                    chunkCount: fileChunks.length,
                                    path: folder?.path,
                                    isCancelled: fileUploadProgress.isCancellationRequested
                                } as ICompleteFileUploadRequest;

                                if (fileUploadProgress.isCancellationRequested) {
                                    // The cancellation completion is best-effort cleanup: its
                                    // failure must not mark the user-cancelled file as failed
                                    // (which would fail the whole batch and block the files that
                                    // uploaded fine). Anything left behind is reclaimed by the
                                    // server's staged-upload sweep.
                                    try {
                                        await apiCompleteChunkUpload(completeUploadRequest);
                                    } catch (error) {
                                        logger(error, "DragAndDropHelper.ts (uploadDroppedFiles - cancellation cleanup)");
                                    }
                                } else {
                                    await apiCompleteChunkUpload(completeUploadRequest);
                                }
                            }
                            else {
                                const formData = new FormData();
                                formData.set("file", fileSystemFile.file);

                                const config: AxiosRequestConfig = {
                                    onUploadProgress: function (progressEvent: any) {
                                        // const percentCompleted = progressEvent.loaded / progressEvent.total;
                                        // setPercentComplete(+percentCompleted.toFixed(2));
                                        // if (percentCompleted === 1) {
                                        //     setUploadStatus(UploadStatus.Finalizing);
                                        // }
                                        fileUploadProgress.progress = progressEvent.loaded / progressEvent.total;
                                    },
                                };

                                await apiUpload(
                                    { accountId, formData, path: folder?.path } as IFileUploadRequest,
                                    config
                                );
                            }

                            fileUploadProgress.progress = 1;
                            return { success: true, name: `${fileEntry.name}.${fileEntry.extension}` } as IFileSystemFileSaveResult;
                        } catch (error) {
                            logger(error, "DragAndDropHelper.ts (uploadDroppedFiles)");
                        }
                    }
                    return { success: false, name: `${fileEntry.name}.${fileEntry.extension}` } as IFileSystemFileSaveResult;
                }));

                return {
                    success: !fileSystemUploadResults.some(result => !result.success),
                    message: "",
                    succeededFiles: fileSystemUploadResults.filter(res => res.success).map(res => ({ name: res.name, uniqueId: "" })),
                    failedFileNames: fileSystemUploadResults.filter(res => !res.success).map(res => res.name)
                } as IFileSaveResult;
            }
        }
        return null;
    }

    static registerExchangeItemUploadProgress(filesToUpload: IFileToUpload[]): void {
        this.mode = DragMode.exchangeItems;
        this.uploadProgress.length = 0;

        filesToUpload.forEach(file => {
            this.uploadProgress.push({
                fileName: file.name,
                fileExtension: file.extension,
                progress: 0
            } as IFileUploadProgress);
        });
    }

    static reset(): void {
        this.isDroppedModeActive = false;
        this.clear();
    }

    static resetGridItems(): void {
        this.sourceDirectory = undefined;
        this.destinationDirectory = undefined;
        this.sourceServerRelPath = "";
        this.selectedGridItems = [];
    }

    private static clear(): void {
        this.fileEntries = [];
        this.folderEntries = [];
    }

    // Names skipped by the zero-byte guard during the current intake (drop/selection),
    // surfaced to the user once per intake by notifySkippedEmptyFiles.
    private static skippedEmptyFileNames: string[] = [];

    // Single owner of the zero-byte intake guard: the server rejects an empty upload with
    // a 400, which would otherwise fail the whole batch save. Returns true when the file
    // must be skipped, and records it for the end-of-intake notice — a log-only skip would
    // silently omit an explicitly picked (possibly truncated) document from a batch that
    // then reports full success.
    private static shouldSkipEmptyFile(file: File | null | undefined, context: string): boolean {
        if (file && file.size > 0) {
            return false;
        }
        const name = file?.name ?? "(unknown)";
        logger(new Error(`Skipped empty file: ${name}`), context);
        this.skippedEmptyFileNames.push(name);
        return true;
    }

    // One notice per intake listing every skipped empty file, so the user knows those
    // documents will NOT be part of the save.
    private static notifySkippedEmptyFiles(): void {
        if (this.skippedEmptyFileNames.length === 0) {
            return;
        }
        customToast.info(`Skipped empty file(s): ${this.skippedEmptyFileNames.join(", ")}`, { autoClose: false });
        this.skippedEmptyFileNames = [];
    }

    private static async traverseFileTree(item: FileSystemEntry, filesToUpload: IFileToUpload[], parentFolderId?: string): Promise<void> {
        if (item.isFile) {
            await new Promise<void>(resolve => {
                (item as FileSystemFileEntry).file((file: File) => {
                    if (this.shouldSkipEmptyFile(file, "DragAndDropHelper.ts (traverseFileTree)")) {
                        resolve();
                        return;
                    }
                    const fileToUpload: IFileToUpload = this.getFileToUpload(file, parentFolderId);
                    filesToUpload.push(fileToUpload);
                    resolve();
                });
            });
        }
        else if (item.isDirectory) {
            const folderToUpload: IFileToUpload = this.getFolderToUpload(item.name, item.fullPath, parentFolderId);
            filesToUpload.push(folderToUpload);

            const directoryReader: FileSystemDirectoryReader = (item as FileSystemDirectoryEntry).createReader();
            // readEntries returns at most ~100 entries per call in Chromium; it must be
            // called repeatedly until it yields an empty batch to read the full directory.
            let entries: FileSystemEntry[];
            do {
                entries = await new Promise<FileSystemEntry[]>((resolve, reject) => {
                    directoryReader.readEntries(resolve, reject);
                });
                for (let i = 0; i < entries.length; i++) {
                    await this.traverseFileTree(entries[i], filesToUpload, folderToUpload.id);
                }
            } while (entries.length > 0);
        }
    }

    private static getFileToUpload(file: File, parentFolderId?: string): IFileToUpload {
        const id: string = uuidv4();
        const name: string = removeExtension(file.name);
        const extension: string = getExtension(file.name)?.trim().toLowerCase();

        const fileToUpload: IFileToUpload = {
            id,
            ewsId: null,
            name,
            extension,
            isAttachment: false,
            isSelected: true,
            isDuplicate: false,
            originalName: name,
            lastModifiedDateTime: new Date(file.lastModified).toISOString(),
            isFileSystemEntry: true,
            parentFolderId: parentFolderId,
            canSaveAsPDF: validSaveAsPdfExtentions.some(ext => ext === extension)
        }

        this.fileEntries.push({ id, file } as IFileSystemFile);

        return fileToUpload;
    }

    private static getFolderToUpload(name: string, path?: string, parentFolderId?: string): IFileToUpload {
        const id: string = uuidv4();
        const folderToUpload: IFileToUpload = {
            id,
            ewsId: null,
            name,
            extension: null,
            isAttachment: false,
            isSelected: true,
            isDuplicate: false,
            isFileSystemEntry: true,
            isFolder: true,
            folderPath: path,
            parentFolderId: parentFolderId
        };

        this.folderEntries.push({ id, name, path, parentFolderId } as IFileSystemFolder);

        return folderToUpload;
    }

    private static reorderData = (data: IFileToUpload[]) => {
        const orderedList: IFileToUpload[] = [];

        data
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
            .forEach(file => {
                if (file.isFolder) {
                    orderedList.push(file);
                    const folderFiles = data.filter(x => !x.isFolder && x.parentFolderId === file.id);
                    orderedList.push(...folderFiles);
                } else if (!file.isFolder && !file.parentFolderId) {
                    orderedList.push(file);
                }
            });

        return orderedList;
    }

    private static formatBytes = (bytes: number): string => {
        if (bytes < 1024) {
            return `${bytes} bytes`;
        } else if (bytes < 1024 * 1024) {
            const kb = bytes / 1024;
            return `${kb.toFixed(2)} KB`;
        } else if (bytes < 1024 * 1024 * 1024) {
            const mb = bytes / (1024 * 1024);
            return `${mb.toFixed(2)} MB`;
        } else {
            const gb = bytes / (1024 * 1024 * 1024);
            return `${gb.toFixed(2)} GB`;
        }
    }
}
